import Link from "next/link";

import { Button } from "@/components/button/button.component";
import { Text } from "@/components/text/text.component";

import { appendQueryString } from "@/utils/url.util";

import {
  FlexibleMonitoringAppPlanCard,
  type FlexibleMonitoringAppSectionBlock,
} from "./flexibleMonitoringAppPlanCard.component";
import { FlexibleMonitoringAppSectionMobileCarousel } from "./flexibleMonitoringAppSectionMobileCarousel.client";

export const FlexibleMonitoringAppSection = ({
  block,
}: {
  block: FlexibleMonitoringAppSectionBlock | null | undefined;
}) => {
  if (!block?.heading || !block.plans?.length) {
    return null;
  }

  const ctaHref = appendQueryString(
    block.ctaLink?.url,
    block.ctaLink?.queryString,
  );
  const showCta = Boolean(block.ctaLabel?.trim() && ctaHref);

  return (
    <section
      data-testid="flexible-monitoring-app-section"
      className="bg-primary-100 w-full py-10 md:py-14">
      <div className="mx-auto flex w-full max-w-[1344px] flex-col gap-10 px-2 sm:px-3 md:px-4">
        <div className="mx-auto flex max-w-5xl flex-col gap-3 text-center">
          <Text variant="h3" component="h2" className="text-greyscale-headline">
            {block.heading}
          </Text>
          <Text
            variant="body1"
            component="p"
            className="text-greyscale-body leading-relaxed">
            {block.subheading}
          </Text>
        </div>

        <div className="md:hidden">
          <FlexibleMonitoringAppSectionMobileCarousel
            plans={block.plans}
            ctaLabel={showCta ? block.ctaLabel?.trim() : undefined}
            ctaHref={showCta ? (ctaHref ?? undefined) : undefined}
            ctaOpenInNewTab={Boolean(block.ctaLink?.openInNewTab)}
          />
        </div>

        <ul className="mx-auto hidden w-full items-stretch md:grid md:grid-cols-3 md:gap-3 lg:gap-5">
          {block.plans.map((plan) =>
            plan?._key ? (
              <li
                key={plan._key}
                className="flex h-full min-h-0 min-w-0 justify-center">
                <FlexibleMonitoringAppPlanCard plan={plan} />
              </li>
            ) : null,
          )}
        </ul>

        {showCta && ctaHref ? (
          //ONNLY SHOW ON DESKTOP, NOT ON MOBILE
          <div className="hidden justify-center md:flex">
            <Button secondary className="w-auto px-8 sm:px-10">
              <Link
                href={ctaHref}
                data-testid="flexible-monitoring-app-cta"
                target={block.ctaLink?.openInNewTab ? "_blank" : undefined}
                rel={
                  block.ctaLink?.openInNewTab
                    ? "noopener noreferrer"
                    : undefined
                }>
                {block.ctaLabel}
              </Link>
            </Button>
          </div>
        ) : null}
      </div>
    </section>
  );
};
